// Print templates API — typed wrappers around the
// /api/v1/print-templates routes (PRINT_TEMPLATE_PLAN.md §3).
//
// The four CRUD functions ride the standard apiClient envelope. The
// render endpoint returns either a binary PDF (`Accept:
// application/pdf` → axios `responseType: 'blob'`) or a self-
// contained HTML document (`Accept: text/html` → `responseType:
// 'text'`). Both bypass the apiClient's `data` envelope by going
// through `apiClient.raw` so the response body is the rendered
// artefact itself, not an `ApiSuccess<T>` wrapper.

import { apiClient } from './client';
import { endpoints } from './endpoints';
import type {
  PrintTemplateInfo,
  PrintTemplatesListResponse,
  PrintTemplateType,
  RenderPrintTemplateRequest,
  UpsertPrintTemplateInput,
} from '../types/api';

export async function getPrintTemplatesList(): Promise<PrintTemplatesListResponse> {
  const res = await apiClient.get<PrintTemplatesListResponse>(
    endpoints.printTemplates.list,
  );
  return res.data;
}

export async function getPrintTemplate(
  type: PrintTemplateType,
): Promise<PrintTemplateInfo> {
  const res = await apiClient.get<{ template: PrintTemplateInfo }>(
    endpoints.printTemplates.detail(type),
  );
  return res.data.template;
}

export async function upsertPrintTemplate(
  type: PrintTemplateType,
  body: UpsertPrintTemplateInput,
): Promise<PrintTemplateInfo> {
  const res = await apiClient.put<
    { template: PrintTemplateInfo },
    UpsertPrintTemplateInput
  >(endpoints.printTemplates.upsert(type), body);
  return res.data.template;
}

export async function revertPrintTemplate(
  type: PrintTemplateType,
): Promise<{ message: string }> {
  const res = await apiClient.delete<{ message: string }>(
    endpoints.printTemplates.revert(type),
  );
  return res.data;
}

export type RenderFormat = 'pdf' | 'html';

/**
 * Render the active print template for `type` against the supplied
 * `context`. The server picks the effective template (tenant
 * override → global default).
 *
 * Returns a `Blob` for PDF mode, a `string` (HTML document) for
 * HTML mode. Both responses bypass the apiClient envelope.
 */
export async function renderPrintTemplate(
  type: PrintTemplateType,
  context: Record<string, unknown>,
  format: RenderFormat,
): Promise<Blob | string> {
  const path = endpoints.printTemplates.render(type);
  const body: RenderPrintTemplateRequest = { context };
  if (format === 'pdf') {
    const res = await apiClient.raw.post(path, body, {
      headers: { Accept: 'application/pdf' },
      responseType: 'blob',
    });
    return res.data as Blob;
  }
  const res = await apiClient.raw.post(path, body, {
    headers: { Accept: 'text/html' },
    responseType: 'text',
  });
  return res.data as string;
}
